/** @jsxImportSource @emotion/react */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useErrorBoundary } from 'react-error-boundary';
import {
  Typography,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  MenuItem,
  Box,
  IconButton
} from '@mui/material';
import SquareIcon from '@mui/icons-material/Square';
import CircleIcon from '@mui/icons-material/Circle';
import CloseIcon from '@mui/icons-material/Close';
import Dayjs from 'dayjs';

import useSsurgoLabel from '@hooks/useSsurgoLabel';
import { useAppTheme } from '@hooks/useAppTheme';
import { RootState } from '@state-mgmt/store';
import {
  PreviousVisitSelection,
  selectFieldVisitWizardMapStyle,
  selectImagery,
  selectImageryEnabled,
  selectSelectedImage,
  setFieldVisitWizardMapStyle,
  setHistoricalVisitsProtocolId,
  setHistoricalVisitsTrialId,
  setImageryEnabled,
  setPreviousVisitsSelection,
  setSelectedImage
} from '@state-mgmt/slices/visitSlice';
import { styles } from './styles';
import { useTranslation } from 'react-i18next';
import { mapStyles } from '@components/MapView/MapView';
import { SwipeableDrawer } from '@mui/material';
import { THEME_MODE } from '@state-mgmt/slices/appSlice';
import useGetHistoricalVisitTrialsAndProtocols from '@hooks/useGetHistoricalVisitTrialsAndProtocols';

export enum MAP_LAYERS {
  TRIAL_AREA = 'trial-area',
  TREATMENT_ZONES = 'treatment-zones',
  SURGO = 'surgo',
  FIELD_NOTES = 'field-notes'
}

export interface Props {
  treatmentZones: { color: string; label: string }[];
  selectedLayers: string[];
  hasTreatmentZone?: boolean;
  showTrialArea?: boolean;
  showSsurgo?: boolean;
  protocolId: string;
  trialId: string;
  displayVisitTrialSelectWithSingleOption?: boolean;
  showHistoricalVisits?: boolean;
  onDrawerMetricsChange?: (metrics: { open: boolean; height: number }) => void;
  onMapLayersChange: (mapLayers: string[]) => void;
  onClose: () => void;
}

const FieldMapOptionsModal = ({
  treatmentZones,
  selectedLayers,
  hasTreatmentZone = false,
  showTrialArea = true,
  showSsurgo = true,
  protocolId,
  trialId,
  onMapLayersChange,
  onClose,
  onDrawerMetricsChange,
  displayVisitTrialSelectWithSingleOption = false,
  showHistoricalVisits = true
}: Props) => {
  const [open, setOpen] = useState<boolean>(false);
  const drawerPaperRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { showBoundary } = useErrorBoundary();
  const dispatch = useDispatch();
  const { themeMode } = useSelector((state: RootState) => state.app);
  const theme = useAppTheme({ mode: themeMode });
  const { previousVisitsSelection } = useSelector((state: RootState) => state.visit);
  const imageryStatus = useSelector(selectImagery);
  const imageryEnabled = useSelector(selectImageryEnabled);
  const selectedImage = useSelector(selectSelectedImage);

  const ssurgoLabel = useSsurgoLabel();
  const [controlledValue, setControlledValue] = useState<string[]>(selectedLayers || []);
  const historicalVisitsTrialId = useSelector((state: RootState) => state.visit.historicalVisitsTrialId);
  const historicalVisitsProtocolId = useSelector((state: RootState) => state.visit.historicalVisitsProtocolId);
  const protocolFieldNotes = useSelector((state: RootState) => state.visit.clientProtocolNotes);
  const { trials: visitTrials, protocols: visitProtocols } = useGetHistoricalVisitTrialsAndProtocols({
    selectedTrialId: historicalVisitsTrialId,
    protocolId: protocolId,
    currentTrialId: trialId
  });
  useEffect(() => {
    setOpen(true);
  }, []);

  useEffect(() => {
    if (!open || !onDrawerMetricsChange) {
      return;
    }

    const measureDrawer = () => {
      if (drawerPaperRef.current) {
        onDrawerMetricsChange({ open: true, height: drawerPaperRef.current.offsetHeight });
      }
    };

    measureDrawer();
    const timeout = setTimeout(measureDrawer, 320);
    const observer = new ResizeObserver(measureDrawer);

    if (drawerPaperRef.current) {
      observer.observe(drawerPaperRef.current);
    }

    return () => {
      clearTimeout(timeout);
      observer.disconnect();
    };
  }, [open, onDrawerMetricsChange]);

  useEffect(() => {
    onMapLayersChange(controlledValue);
  }, [controlledValue]);

  const handleOnMapLayersChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const value = event.target.value as string;
      controlledValue.includes(value)
        ? setControlledValue(controlledValue.filter((mapLayer: string) => mapLayer !== value))
        : setControlledValue([...controlledValue, value]);
    } catch (error) {
      showBoundary(error);
    }
  };

  const handleOnPreviousVisitSelectionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const value = event.target.value as string;
      const result = previousVisitsSelection.map(previousVisit =>
        previousVisit.id === value ? { ...previousVisit, selected: !previousVisit.selected } : previousVisit
      );
      dispatch(setPreviousVisitsSelection(result));
    } catch (error) {
      showBoundary(error);
    }
  };

  const mapStyle = useSelector(selectFieldVisitWizardMapStyle);
  const onMapStyleChange = (e: SelectChangeEvent) => {
    dispatch(setFieldVisitWizardMapStyle(e.target.value));
  };

  const onVisitTrialChange = (e: SelectChangeEvent) => {
    const trialId = e.target.value;
    dispatch(setHistoricalVisitsTrialId(e.target.value));

    const t = visitTrials.find(vt => vt.id === trialId);

    if (t?.defaultProtocolId) {
      dispatch(setHistoricalVisitsProtocolId(t.defaultProtocolId));
    } else {
      dispatch(setHistoricalVisitsProtocolId(null));
    }
  };

  const onVisitProtolChange = (e: SelectChangeEvent) => {
    dispatch(setHistoricalVisitsProtocolId(e.target.value));
  };

  const handleChangeImageryEnabled = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      dispatch(setImageryEnabled(checked));
    },
    [dispatch]
  );

  const onSelectedImageChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      dispatch(setSelectedImage(event.target.value));
    },
    [dispatch]
  );

  const hasFieldNote = useMemo(() => {
    return !!protocolFieldNotes?.some(fn => {
      return fn.trial_id === historicalVisitsTrialId && fn.protocol_id === historicalVisitsProtocolId && !!fn.note_count;
    });
  }, [protocolFieldNotes, historicalVisitsProtocolId, historicalVisitsTrialId]);

  const onDrawerClose = () => {
    onDrawerMetricsChange?.({ open: false, height: 0 });
    setOpen(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const showVisitTrialSelect = visitTrials?.length > 1 || (visitTrials?.length === 1 && displayVisitTrialSelectWithSingleOption);
  const showVisitProtocolSelect = visitProtocols?.length > 1 || (visitProtocols?.length === 1 && displayVisitTrialSelectWithSingleOption);
  const showVisitsNotesSection =
    showHistoricalVisits && (showVisitTrialSelect || showVisitProtocolSelect || !!visitTrials?.length || !!previousVisitsSelection?.length);
  const showMapLayersSection = showTrialArea || hasTreatmentZone || showSsurgo || hasFieldNote || !!imageryStatus?.length;

  const checkboxLabel = (text: string) => <Typography sx={{ fontSize: '14px' }}>{text}</Typography>;

  const cardTitle = (text: string) => (
    <Typography color="text.primary" css={styles.cardTitle}>
      {text}
    </Typography>
  );

  const NoDataWarning = () => {
    if (!visitTrials?.length) return null;

    const hasVisits = !!previousVisitsSelection?.length;
    const hasNotes = hasFieldNote;

    if (hasVisits && hasNotes) return null;

    let message: string = '';

    if (!hasVisits && !hasNotes) {
      message = t('map-options.no-visits-notes-warning');
    } else if (!hasVisits) {
      message = t('map-options.no-visits-warning');
    } else if (!hasNotes) {
      message = t('map-options.no-notes-warning');
    }

    return <Typography css={styles.warningBox(theme, themeMode)}>{message}</Typography>;
  };

  return (
    <SwipeableDrawer
      sx={{}}
      anchor="bottom"
      open={open}
      onClose={onDrawerClose}
      onOpen={() => {}}
      disableDiscovery={true}
      disableSwipeToOpen={true}
      hideBackdrop
      disableEnforceFocus
      disableRestoreFocus
      disableScrollLock
      PaperProps={{
        ref: drawerPaperRef,
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '65vh',
          minHeight: '50vh',
          pointerEvents: 'auto',
          mx: 'auto',
          backgroundColor: themeMode === THEME_MODE.DARK ? '#1F2128' : '#fff'
        }
      }}
      ModalProps={{
        BackdropProps: {
          invisible: true
        },
        sx: {
          pointerEvents: 'none'
        }
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          paddingTop: 2
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '25%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 40,
            height: 4,
            bgcolor: 'text.disabled',
            borderRadius: 2
          }}
        />

        <Typography variant="subtitle1">{t('map-options.title')}</Typography>

        <IconButton onClick={onDrawerClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <div id="map-options-modal-container" css={styles.scrollableArea(theme)}>
        <Card variant="outlined" css={styles.card(theme, themeMode)}>
          <CardContent css={styles.cardContent}>
            {cardTitle(t('map-options.base-map-title'))}

            <FormControl size="small" fullWidth sx={{ fieldset: { legend: { maxWidth: '100%' } } }}>
              <InputLabel variant="outlined" htmlFor="base-layer-input" shrink={true}>
                {t('map-options.base-layer')}
              </InputLabel>

              <Select
                id="base-layer-input"
                value={mapStyle || mapStyles.satellite}
                label={t('map-options.base-layer')}
                onChange={onMapStyleChange}
                sx={{ fontSize: '14px' }}
              >
                <MenuItem value={mapStyles.satellite}>{t('map-options.base-layer-satellite')}</MenuItem>
                <MenuItem value={mapStyles.dark}>{t('map-options.base-layer-dark')}</MenuItem>
                <MenuItem value={mapStyles.light}>{t('map-options.base-layer-light')}</MenuItem>
                <MenuItem value={mapStyles.streets}>{t('map-options.base-layer-streets')}</MenuItem>
                <MenuItem value={mapStyles.outdoors}>{t('map-options.base-layer-outdoors')}</MenuItem>
              </Select>
            </FormControl>
          </CardContent>
        </Card>

        {showMapLayersSection ? (
          <Card variant="outlined" css={styles.card(theme, themeMode)}>
            <CardContent css={styles.cardContent}>
              {cardTitle(t('map-options.layers-title'))}

              <Box css={styles.checkboxGroup}>
                {showTrialArea ? (
                  <FormControlLabel
                    css={styles.formControlLabel}
                    control={
                      <Checkbox value={MAP_LAYERS.TRIAL_AREA} checked={controlledValue.includes(MAP_LAYERS.TRIAL_AREA)} onChange={handleOnMapLayersChange} />
                    }
                    label={checkboxLabel(t('map-options.trial-area-label'))}
                  />
                ) : null}

                {hasTreatmentZone ? (
                  <FormControlLabel
                    css={styles.formControlLabel}
                    control={
                      <Checkbox
                        value={MAP_LAYERS.TREATMENT_ZONES}
                        checked={controlledValue.includes(MAP_LAYERS.TREATMENT_ZONES)}
                        onChange={handleOnMapLayersChange}
                      />
                    }
                    label={checkboxLabel(t('map-options.treatment-zones-label'))}
                  />
                ) : null}

                {showSsurgo ? (
                  <FormControlLabel
                    css={styles.formControlLabel}
                    control={<Checkbox value="surgo" checked={controlledValue.includes('surgo')} onChange={handleOnMapLayersChange} />}
                    label={checkboxLabel(ssurgoLabel)}
                  />
                ) : null}

                {hasFieldNote ? (
                  <FormControlLabel
                    css={styles.formControlLabel}
                    control={
                      <Checkbox value={MAP_LAYERS.FIELD_NOTES} checked={controlledValue.includes(MAP_LAYERS.FIELD_NOTES)} onChange={handleOnMapLayersChange} />
                    }
                    label={checkboxLabel(t('map-options.field-notes-label'))}
                  />
                ) : null}

                {imageryStatus?.length ? (
                  <FormControlLabel
                    css={styles.formControlLabel}
                    control={<Checkbox checked={imageryEnabled} onChange={handleChangeImageryEnabled} />}
                    label={checkboxLabel(t('map-options.imagery'))}
                  />
                ) : null}
              </Box>

              {imageryStatus?.length ? (
                <FormControl
                  size="small"
                  fullWidth
                  css={styles.nestedControl(theme)}
                  sx={{ fieldset: { legend: { maxWidth: '100%' } } }}
                  disabled={!imageryEnabled}
                >
                  <InputLabel variant="outlined" htmlFor="image-date-input" shrink={true}>
                    {t('map-options.image-date')}
                  </InputLabel>

                  <Select
                    id="image-date-input"
                    value={selectedImage || ''}
                    label={t('map-options.image-date')}
                    onChange={onSelectedImageChange}
                    sx={{ fontSize: '14px' }}
                  >
                    {imageryStatus.map(status => {
                      return (
                        <MenuItem value={status.id || ''} key={status.id || ''}>
                          {Dayjs(status.date).format('MM/DD/YYYY')}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              ) : null}
            </CardContent>
          </Card>
        ) : null}

        {showVisitsNotesSection ? (
          <Card variant="outlined" css={styles.card(theme, themeMode)}>
            <CardContent css={styles.cardContent}>
              {cardTitle(t('map-options.visits-notes-title'))}

              <Box css={styles.controlGroup(theme)}>
                {showVisitTrialSelect ? (
                  <FormControl size="small" fullWidth sx={{ fieldset: { legend: { maxWidth: '100%' } } }}>
                    <InputLabel variant="outlined" htmlFor="visit-trial-input" shrink={true}>
                      {t('map-options.visit-trial')}
                    </InputLabel>

                    <Select
                      id="visit-trial-input"
                      value={historicalVisitsTrialId || ''}
                      label={t('map-options.visit-trial')}
                      onChange={onVisitTrialChange}
                      sx={{ fontSize: '14px' }}
                    >
                      {visitTrials.map(trialOption => (
                        <MenuItem key={trialOption.id} value={trialOption.id}>
                          {trialOption.year}: {trialOption.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : null}

                {showVisitProtocolSelect ? (
                  <FormControl size="small" fullWidth sx={{ fieldset: { legend: { maxWidth: '100%' } } }}>
                    <InputLabel variant="outlined" htmlFor="visit-protocol-input" shrink={true}>
                      {t('map-options.visit-protocol')}
                    </InputLabel>

                    <Select
                      id="visit-protocol-input"
                      value={historicalVisitsProtocolId || ''}
                      label={t('map-options.visit-protocol')}
                      onChange={onVisitProtolChange}
                      sx={{ fontSize: '14px' }}
                    >
                      {visitProtocols.map(protocolOption => (
                        <MenuItem key={protocolOption.id} value={protocolOption.id}>
                          {protocolOption.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : null}

                {previousVisitsSelection.length ? (
                  <Box css={styles.checkboxGroup}>
                    {previousVisitsSelection.map(visit => (
                      <FormControlLabel
                        key={visit.id}
                        css={styles.formControlLabel}
                        control={<Checkbox value={visit.id} checked={visit.selected} onChange={handleOnPreviousVisitSelectionChange} />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <CircleIcon sx={{ color: visit.color, fontSize: '18px' }} />
                            <Typography sx={{ fontSize: '14px' }}>
                              {visit.label} ({Dayjs(visit.completedAt).format('MM/DD/YYYY')})
                            </Typography>
                          </Box>
                        }
                      />
                    ))}
                  </Box>
                ) : null}

                <NoDataWarning />
              </Box>
            </CardContent>
          </Card>
        ) : null}

        {treatmentZones.length > 0 ? (
          <Card variant="outlined" css={styles.card(theme, themeMode)}>
            <CardContent css={styles.cardContent}>
              {cardTitle(t('map-options.map-legend-title'))}

              <div css={styles.treatmentZonesContainer}>
                {treatmentZones.map(treatmentZone => (
                  <div css={styles.treatmentZone} key={treatmentZone.color}>
                    <SquareIcon css={styles.treatmentZoneBullet(treatmentZone.color)} />
                    <Typography css={styles.treatmentZoneLabel}>{treatmentZone.label}</Typography>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </SwipeableDrawer>
  );
};

export default FieldMapOptionsModal;
